import React, { useRef, useCallback } from 'react';
import TailwindLineChart from '../Tailwind/TailwindLineChart';
import TailwindBarChart from '../Tailwind/TailwindBarChart';
import TailwindPieChart from '../Tailwind/TailwindPieChart';
import { CopyImageButton } from './CopyButton';
import type { ChartSpec } from '../../hooks/useAgentChat';

interface AgentChartProps {
    chart: ChartSpec;
}

export default function AgentChart({ chart }: AgentChartProps) {
    const chartRef = useRef<HTMLDivElement>(null);
    const getChartEl = useCallback(() => chartRef.current, []);
    if (!chart || !chart.chart_type) {
        return <p className="text-xs text-slate-400 italic">Chart data unavailable</p>;
    }

    const renderChart = () => {
        switch (chart.chart_type) {
            case 'line':
                if (!chart.data?.length || !chart.series?.length) {
                    return <p className="text-xs text-slate-400 italic">No data to display</p>;
                }
                return (
                    <TailwindLineChart
                        data={chart.data}
                        series={chart.series.map(s => ({
                            dataKey: s.dataKey,
                            label: s.label,
                            color: s.color,
                        }))}
                        xAxisKey={chart.x_axis_key}
                        height={250}
                        showGrid={true}
                        showLegend={true}
                        showTooltip={true}
                    />
                );
            case 'bar':
                if (!chart.data?.length || !chart.series?.length) {
                    return <p className="text-xs text-slate-400 italic">No data to display</p>;
                }
                return (
                    <TailwindBarChart
                        data={chart.data}
                        series={chart.series.map(s => ({
                            dataKey: s.dataKey,
                            label: s.label,
                            color: s.color,
                        }))}
                        xAxisKey={chart.x_axis_key}
                        height={250}
                        stacked={chart.stacked}
                        showGrid={true}
                        showLegend={true}
                        showTooltip={true}
                    />
                );
            case 'pie':
                if (!chart.pie_data?.length) {
                    return <p className="text-xs text-slate-400 italic">No data to display</p>;
                }
                return (
                    <TailwindPieChart
                        data={chart.pie_data.map(d => ({
                            name: d.name,
                            value: d.value,
                            color: d.color,
                        }))}
                        height={250}
                        innerRadius={40}
                        outerRadius={90}
                        showLabels={true}
                        showLegend={true}
                        showTooltip={true}
                    />
                );
            default:
                return <p className="text-xs text-red-500">Unknown chart type</p>;
        }
    };

    return (
        <div ref={chartRef} className="my-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm group/chart relative">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-slate-700">{chart.title}</h4>
                <div className="opacity-0 group-hover/chart:opacity-100 transition-opacity">
                    <CopyImageButton getElement={getChartEl} title="Copy chart as image" />
                </div>
            </div>
            <div className="w-full min-w-0">
                {renderChart()}
            </div>
        </div>
    );
}
